import { createContext } from "preact";
import { useContext, useMemo, useState, useCallback, useEffect, useRef } from "preact/hooks";
import { fetchToServer, getWsHub } from "@shared/components";

function makeUrl(role, id) {
  return `/${role}/api/projects/${id}`;
}

function mergePatch(base, patch) {
  if (!patch) return base;
  const out = { ...(base || {}) };
  for (const k of Object.keys(patch)) {
    const pv = patch[k];
    if (Array.isArray(pv)) out[k] = pv.slice();
    else if (pv && typeof pv === "object") out[k] = { ...(out[k] || {}), ...pv };
    else out[k] = pv;
  }
  return out;
}

const ProjectDetailPageContext = createContext(null);

export function ProjectDetailPageProvider({ projectId, role, children }) {
  const [project, setProject] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pendingPaymentIdForDelivery, setPendingPaymentIdForDelivery] = useState(null);

  const patchQueueRef = useRef([]);
  const rafRef = useRef(null);
  const wsBoundRef = useRef({ id: null });

  const load = useCallback(async (id, r) => {
    if (!id || !r) return;
    setIsLoading(true);
    setError(null);
    try {
      const response = await fetchToServer({ url: makeUrl(r, id), method: 'GET' });
      setProject(response.data);
    } catch (err) {
      setError(err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    load(projectId, role);
  }, [projectId, role, load]);

  useEffect(() => {
    if (!projectId) return;

    console.log(`[WS] Hook de efecto para projectId: ${projectId}`);
    const hub = getWsHub();
    const socket = hub.ns("/ws/projects");

    const thisBind = { id: projectId };
    wsBoundRef.current = thisBind;

    console.log(`[WS] Emitiendo 'subscribe' para projectId: ${projectId}`);
    socket.emit("subscribe", { projectId });

    const onPatch = (patch) => {
      console.log(`[WS] Recibido 'project:patch' para projectId: ${projectId}`, patch);
      if (wsBoundRef.current.id !== projectId) {
        console.log("[WS] Patch ignorado, el ID del componente cambió.");
        return;
      }

      patchQueueRef.current.push(patch);
      if (rafRef.current) return;
      rafRef.current = requestAnimationFrame(() => {
        const queue = patchQueueRef.current;
        patchQueueRef.current = [];
        rafRef.current = null;

        if (!queue.length) return;
        console.log("[WS] Procesando cola de patches:", queue);
        const merged = queue.reduce((acc, p) => mergePatch(acc, p), {});
        setProject((prev) => mergePatch(prev, merged));
      });
    };

    console.log(`[WS] Registrando listener para 'project:patch'`);
    socket.on("project:patch", onPatch);

    return () => {
      console.log(`[WS] Limpiando hook de efecto para projectId: ${projectId}`);
      socket.emit("unsubscribe", { projectId });
      socket.off("project:patch", onPatch);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      patchQueueRef.current = [];
      rafRef.current = null;
    };
  }, [projectId]);

  const value = useMemo(() => ({
    projectId,
    project,
    role,
    isLoading,
    error,
    fetchProject: () => load(projectId, role),
    applySessionHeader: () => {}, // Placeholder for compatibility
    pendingPaymentIdForDelivery,
    setPendingPaymentIdForDelivery,
  }), [projectId, project, role, isLoading, error, load, pendingPaymentIdForDelivery]);

  return (
    <ProjectDetailPageContext.Provider value={value}>
      {children}
    </ProjectDetailPageContext.Provider>
  );
}

export const useProjectDetailPage = () => {
  const context = useContext(ProjectDetailPageContext);
  if (!context) {
    throw new Error("useProjectDetailPage must be used within a ProjectDetailPageProvider");
  }
  return context;
};